import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Map, Filter, Layers, X } from 'lucide-react';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker icons
const createIcon = (color) => new L.DivIcon({
  html: `<div style="background:${color};width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -14],
  className: '',
});

const createSpecialIcon = (emoji) => new L.DivIcon({
  html: `<div style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:18px;background:white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${emoji}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -16],
  className: '',
});

const ICONS = {
  violation_pending: createIcon('#E63946'),
  violation_verified: createIcon('#E63946'),
  violation_in_progress: createIcon('#E63946'),
  violation_resolved: createIcon('#2A9D8F'),
  blackspot_pending: createIcon('#F4A261'),
  blackspot_verified: createIcon('#F4A261'),
  blackspot_in_progress: createIcon('#F4A261'),
  blackspot_resolved: createIcon('#2A9D8F'),
  signal: createSpecialIcon('🚦'),
  checkpoint: createSpecialIcon('👮'),
  accident_zone: createSpecialIcon('⚠️'),
};

// Static landmarks for Kalaburagi
const LANDMARKS = [
  { type: 'signal', lat: 17.3297, lng: 76.8343, label: 'Jagat Circle Signal' },
  { type: 'signal', lat: 17.3320, lng: 76.8290, label: 'Super Market Signal' },
  { type: 'signal', lat: 17.3350, lng: 76.8400, label: 'University Road Signal' },
  { type: 'signal', lat: 17.3280, lng: 76.8250, label: 'Station Road Signal' },
  { type: 'signal', lat: 17.3260, lng: 76.8380, label: 'Humnabad Cross Signal' },
  { type: 'checkpoint', lat: 17.3310, lng: 76.8310, label: 'Police Checkpoint - Jagat' },
  { type: 'checkpoint', lat: 17.3370, lng: 76.8450, label: 'Police Checkpoint - Aland Road' },
  { type: 'checkpoint', lat: 17.3240, lng: 76.8200, label: 'Police Checkpoint - Ring Road' },
  { type: 'accident_zone', lat: 17.3290, lng: 76.8320, label: 'Accident Zone - Jagat Circle' },
  { type: 'accident_zone', lat: 17.3340, lng: 76.8430, label: 'Accident Zone - University Gate' },
  { type: 'accident_zone', lat: 17.3220, lng: 76.8180, label: 'Accident Zone - Ring Road Curve' },
];

function getIcon(report) {
  const key = `${report.report_type}_${report.status}`;
  return ICONS[key] || ICONS.violation_pending;
}

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, 13);
  }, [center, map]);
  return null;
}

export default function MapDashboardPage() {
  const [reports, setReports] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showLandmarks, setShowLandmarks] = useState(true);

  // Default center: Gulbarga/Kalaburagi
  const defaultCenter = [17.3297, 76.8343];

  useEffect(() => {
    axios.get(`${API}/reports?limit=500`).then(r => {
      setReports(r.data.reports || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    let f = reports;
    if (typeFilter !== 'all') f = f.filter(r => r.report_type === typeFilter);
    if (statusFilter !== 'all') f = f.filter(r => r.status === statusFilter);
    setFiltered(f);
  }, [reports, typeFilter, statusFilter]);

  const geoReports = filtered.filter(r => r.latitude && r.longitude);

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="map-dashboard-page">
      {/* Top bar */}
      <div className="glass border-b px-4 md:px-8 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <Map className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-bold">Live Traffic Map</h1>
          <Badge variant="outline" className="text-xs">{geoReports.length} reports</Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => setShowFilters(!showFilters)}
          data-testid="toggle-map-filters"
        >
          <Filter className="w-4 h-4" />{showFilters ? 'Hide' : 'Filters'}
        </Button>
        <Button
          variant={showLandmarks ? "default" : "outline"}
          size="sm"
          className="gap-2"
          onClick={() => setShowLandmarks(!showLandmarks)}
          data-testid="toggle-landmarks"
        >
          <Layers className="w-4 h-4" />Landmarks
        </Button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="glass border-b px-4 md:px-8 py-3 flex flex-wrap gap-3 z-10 animate-fade-in" data-testid="map-filters-panel">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40" data-testid="map-type-filter">
              <SelectValue placeholder="Report Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="violation">Violations</SelectItem>
              <SelectItem value="blackspot">Blackspots</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="map-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => { setTypeFilter('all'); setStatusFilter('all'); }} data-testid="map-clear-filters">
            <X className="w-4 h-4 mr-1" />Clear
          </Button>
          {/* Legend */}
          <div className="ml-auto flex items-center gap-3 text-xs flex-wrap">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#E63946]" />Violation</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#F4A261]" />Blackspot</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#2A9D8F]" />Resolved</span>
            <span className="flex items-center gap-1">🚦 Signal</span>
            <span className="flex items-center gap-1">👮 Checkpoint</span>
            <span className="flex items-center gap-1">⚠️ Accident Zone</span>
          </div>
        </div>
      )}

      {/* Map */}
      <div className="flex-1 relative">
        <MapContainer
          center={defaultCenter}
          zoom={13}
          className="w-full h-full"
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {geoReports.map(report => (
            <Marker
              key={report.id}
              position={[report.latitude, report.longitude]}
              icon={getIcon(report)}
            >
              <Popup maxWidth={280}>
                <div className="text-sm space-y-1">
                  <div className="font-bold">{report.category}</div>
                  <div className="text-xs text-gray-500 uppercase">{report.report_type} &middot; {report.status}</div>
                  {report.description && <p className="text-xs">{report.description}</p>}
                  {report.vehicle_number && <p className="text-xs font-mono">Vehicle: {report.vehicle_number}</p>}
                  <p className="text-xs text-gray-400">{new Date(report.created_at).toLocaleString()}</p>
                  {report.media_url && (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${report.media_url}`} alt="Evidence" className="w-full h-24 object-cover rounded mt-1" />
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
          {showLandmarks && LANDMARKS.map((lm, i) => (
            <Marker
              key={`lm-${i}`}
              position={[lm.lat, lm.lng]}
              icon={ICONS[lm.type]}
            >
              <Popup>
                <div className="text-sm">
                  <div className="font-bold">{lm.label}</div>
                  <div className="text-xs text-gray-500 capitalize">{lm.type.replace('_', ' ')}</div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
